import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, ShoppingBag, Tag } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";

const formatMoneyOrFree = (amount, currency) =>
  Number(amount || 0) === 0 ? "Free" : formatMoney(amount, currency);
import { useStorefrontCart, getSavedCustomer, saveCustomer } from "@/lib/storefrontCart";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function StorefrontCheckout() {
  const { username } = useParams();
  const cart = useStorefrontCart(username);
  const [store, setStore] = useState(null);
  const [storeStatus, setStoreStatus] = useState("loading"); // loading | ok | error

  const saved = useMemo(() => getSavedCustomer() || {}, []);
  const [name, setName] = useState(saved.name || "");
  const [phone, setPhone] = useState(saved.phone || "");
  const [email, setEmail] = useState(saved.email || "");
  const [line1, setLine1] = useState(saved.line1 || "");
  const [city, setCity] = useState(saved.city || "");
  const [addrState, setAddrState] = useState(saved.state || "");
  const [country, setCountry] = useState(saved.country || "India");
  const [postalCode, setPostalCode] = useState(saved.postal_code || "");

  const [discountCode, setDiscountCode] = useState("");
  const [appliedCode, setAppliedCode] = useState(""); // the code actually reflected in `preview`
  const [discountStatus, setDiscountStatus] = useState("idle"); // idle | checking | applied | error
  const [discountError, setDiscountError] = useState("");

  // Full price breakdown -- subtotal, shipping, discount, total. Fetched
  // automatically (no discount code required) so the customer always sees
  // the real, shipping-inclusive total before they ever hit "Pay now."
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  // Order confirmation step: populated once /cart-checkout creates the DB
  // order, shown as a "review before you pay" card -- Razorpay only opens
  // after the customer explicitly confirms here.
  const [checkoutData, setCheckoutData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [placing, setPlacing] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [screenshotError, setScreenshotError] = useState("");
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState(null);
  const [returnStatus, setReturnStatus] = useState(null); // null | "confirming"
  const handledReturn = useRef(false);

  const previewRequestId = useRef(0);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/public/store", { params: { u: (username || "").toLowerCase() } })
      .then(({ data }) => {
        if (cancelled) return;
        setStore(data);
        setStoreStatus("ok");
      })
      .catch(() => {
        if (cancelled) return;
        setStoreStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [username]);

  // Returning from Stripe / PayPal: ?order_id=..&payment=success|cancelled.
  // The webhook marks the order paid a few seconds after the redirect, so on
  // success we poll the order status briefly before showing the confirmation.
  useEffect(() => {
    if (handledReturn.current) return;
    const params = new URLSearchParams(window.location.search);
    const returnOrderId = params.get("order_id");
    const payment = params.get("payment");
    if (!returnOrderId || !payment) return;
    handledReturn.current = true;
    window.history.replaceState({}, "", window.location.pathname);

    if (payment === "cancelled") {
      toast.error("Payment was cancelled. Your cart is still here — you can try again.");
      return;
    }
    if (payment !== "success") return;

    setReturnStatus("confirming");
    (async () => {
      let result = null;
      const delays = [2500, 4000, 6000]; // max 3 checks, ~12s total
      for (let i = 0; i < delays.length; i++) {
        await new Promise((r) => setTimeout(r, delays[i]));
        try {
          const { data } = await api.get("/public/cart-order-status", {
            params: { order_id: returnOrderId, u: username },
          });
          result = data;
          if (data.paid) break;
        } catch (err) {
          if (err?.response?.status === 404) break;
        }
      }
      setReturnStatus(null);
      if (!result) {
        toast.error("We couldn't confirm your payment here. If you were charged, please message the seller.");
        return;
      }
      cart.clear();
      setSuccess({ order_id: returnOrderId, order_number: result.order_number, processing: !result.paid });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const currency = store?.currency || "INR";

  const DISCOUNT_ERROR_MESSAGES = {
    already_used: "You've already used this code once — it can't be applied again.",
    invalid_code: "That code isn't valid or has expired.",
    not_eligible: "Your cart doesn't meet the minimum for this code yet.",
  };

  // Cart line items in the exact shape the backend wants. Recomputed
  // whenever the underlying cart contents actually change.
  const cartPayload = useMemo(
    () =>
      cart.items.map((i) => ({
        catalog_item_id: i.catalog_item_id,
        quantity: i.quantity,
        size: i.size || "",
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cart.items.map((i) => `${i.catalog_item_id}:${i.size || ""}:${i.quantity}`).join("|")]
  );

  const fetchPreview = async (code) => {
    if (cartPayload.length === 0) {
      setPreview(null);
      setPreviewError(false);
      return null;
    }
    const requestId = ++previewRequestId.current;
    setPreviewLoading(true);
    setPreviewError(false);
    try {
      const { data } = await api.post("/public/cart-discount-preview", {
        username,
        cart: cartPayload,
        customer: { phone: phone.trim(), email: email.trim() },
        discount_code: code || "",
      });
      if (requestId !== previewRequestId.current) return null; // stale response, ignore
      setPreview(data);
      return data;
    } catch (err) {
      if (requestId === previewRequestId.current) {
        setPreview(null);
        setPreviewError(true);
      }
      // Surfaced in the console so a merchant/dev checking a stuck checkout
      // can see the real cause instead of just "it's frozen."
      console.error("cart-discount-preview failed:", err);
      return null;
    } finally {
      if (requestId === previewRequestId.current) setPreviewLoading(false);
    }
  };

  // Auto-refresh the price breakdown (shipping included) any time the cart
  // itself changes, using whatever discount code is currently applied (or
  // none). This is what makes shipping visible even if the customer never
  // touches the discount field at all.
  useEffect(() => {
    fetchPreview(appliedCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartPayload]);

  const applyDiscountCode = async () => {
    if (!discountCode.trim()) return;
    setDiscountStatus("checking");
    setDiscountError("");
    const data = await fetchPreview(discountCode.trim());
    if (!data) {
      setDiscountStatus("error");
      setDiscountError("Couldn't check this code right now. Please try again.");
      return;
    }
    setAppliedCode(discountCode.trim());
    if (data.valid) {
      setDiscountStatus("applied");
    } else {
      setDiscountStatus("error");
      setDiscountError(DISCOUNT_ERROR_MESSAGES[data.discount_error] || "Couldn't apply this code.");
    }
  };

  const clearDiscount = () => {
    setDiscountCode("");
    setAppliedCode("");
    setDiscountStatus("idle");
    setDiscountError("");
    fetchPreview("");
  };

  const startCheckout = async (e) => {
    e.preventDefault();
    if (placing || cart.items.length === 0) return;
    if (!name.trim() || !email.trim() || (cart.hasPhysical && !phone.trim())) {
      toast.error(cart.hasPhysical ? "Name, phone, and email are required." : "Name and email are required.");
      return;
    }
    if (cart.hasPhysical && (!line1.trim() || !city.trim() || !addrState.trim() || !country.trim() || !postalCode.trim())) {
      toast.error("Please fill in the full shipping address.");
      return;
    }
    setErrors([]);
    setPlacing(true);
    try {
      const isFreeCart = cart.items.length > 0 && cart.items.every((l) => l.is_digital && Number(l.price) === 0);
      const usesRazorpay = !isFreeCart && !["upi", "qr", "bank", "stripe", "paypal"].includes(store?.payment_method);
      if (usesRazorpay) {
        const ok = await loadRazorpay();
        if (!ok) throw new Error("razorpay_load_failed");
      }

      const { data } = await api.post("/public/cart-checkout", {
        username,
        cart: cartPayload,
        customer: { name: name.trim(), phone: phone.trim(), email: email.trim() },
        shipping_address: cart.hasPhysical
          ? {
              line1: line1.trim(),
              city: city.trim(),
              state: addrState.trim(),
              country: country.trim(),
              postal_code: postalCode.trim(),
            }
          : {},
        discount_code: appliedCode,
      });

      // Free digital order: already claimed and delivered by the server -- no payment step.
      if (data.free) {
        saveCustomer({
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim(),
          line1: line1.trim(),
          city: city.trim(),
          state: addrState.trim(),
          country: country.trim(),
          postal_code: postalCode.trim(),
        });
        cart.clear();
        setSuccess({ order_id: data.order_id, order_number: data.order_number, free: true });
        return;
      }

      // Order now exists in the DB with a real order_number. Show the
      // review-and-confirm card instead of opening Razorpay immediately.
      setCheckoutData(data);
      setConfirmOpen(true);
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === "cart_invalid") {
        setErrors(err.response.data.details || ["Your cart has an issue — please review it."]);
      } else {
        toast.error("Couldn't start checkout. Please try again.");
      }
    } finally {
      setPlacing(false);
    }
  };

  const proceedToPayment = () => {
    const data = checkoutData;
    if (!data) return;
    setPlacing(true);

    // Stripe / PayPal: full-page redirect to the hosted payment page. The
    // webhook marks the order paid; we confirm on return (see the effect above).
    if (data.checkout_url) {
      saveCustomer({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        line1: line1.trim(),
        city: city.trim(),
        state: addrState.trim(),
        country: country.trim(),
        postal_code: postalCode.trim(),
      });
      window.location.href = data.checkout_url;
      return;
    }

    const options = {
      key: data.key_id,
      amount: data.amount,
      currency: data.currency,
      name: store?.ig_username ? `@${store.ig_username}` : "Checkout",
      description: `${cart.items.length} item${cart.items.length === 1 ? "" : "s"}`,
      order_id: data.razorpay_order_id,
      prefill: data.prefill,
      theme: { color: "#FF3366" },
      handler: async (resp) => {
        try {
          await api.post("/public/cart-verify", {
            order_id: data.order_id,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          saveCustomer({
            name: name.trim(),
            phone: phone.trim(),
            email: email.trim(),
            line1: line1.trim(),
            city: city.trim(),
            state: addrState.trim(),
            country: country.trim(),
            postal_code: postalCode.trim(),
          });
          cart.clear();
          setConfirmOpen(false);
          setSuccess({ order_id: data.order_id, order_number: data.order_number });
        } catch {
          toast.error("Payment went through but we couldn't confirm it here. Please message the seller to confirm your order.");
        } finally {
          setPlacing(false);
        }
      },
      modal: { ondismiss: () => setPlacing(false) },
    };
    const rp = new window.Razorpay(options);
    rp.on("payment.failed", () => {
      toast.error("Payment failed. Please try again.");
      setPlacing(false);
    });
    rp.open();
  };

  const MAX_SCREENSHOT_BYTES = 8 * 1024 * 1024; // 8MB

  const handleScreenshotChange = (e) => {
    const file = e.target.files?.[0];
    setScreenshotError("");
    if (!file) {
      setScreenshotFile(null);
      setScreenshotPreview(null);
      return;
    }
    if (!file.type.startsWith("image/")) {
      setScreenshotError("Please choose an image file.");
      setScreenshotFile(null);
      setScreenshotPreview(null);
      return;
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      setScreenshotError("That image is too large — please choose one under 8MB.");
      setScreenshotFile(null);
      setScreenshotPreview(null);
      return;
    }
    setScreenshotFile(file);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("read_failed"));
      reader.readAsDataURL(file);
    });

  const markAsPaid = async () => {
    if (!checkoutData || markingPaid) return;
    if (!screenshotFile) {
      setScreenshotError("Please attach a screenshot of your payment.");
      return;
    }
    setMarkingPaid(true);
    try {
      const screenshotDataUrl = await fileToBase64(screenshotFile);
      await api.post("/public/cart-mark-paid", {
        order_id: checkoutData.order_id,
        username,
        screenshot_base64: screenshotDataUrl,
        screenshot_mime: screenshotFile.type,
      });
      saveCustomer({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        line1: line1.trim(),
        city: city.trim(),
        state: addrState.trim(),
        country: country.trim(),
        postal_code: postalCode.trim(),
      });
      cart.clear();
      setConfirmOpen(false);
      setScreenshotFile(null);
      setScreenshotPreview(null);
      setSuccess({ order_id: checkoutData.order_id, order_number: checkoutData.order_number, pending: true });
    } catch (e) {
      if (e?.response?.status === 400) {
        setScreenshotError("Please attach a screenshot of your payment.");
      } else {
        toast.error("Couldn't confirm your payment right now. Please try again.");
      }
    } finally {
      setMarkingPaid(false);
    }
  };

  if (returnStatus === "confirming") {
    return (
      <section className="flex min-h-screen items-center justify-center bg-cream px-6 py-12">
        <div className="w-full max-w-sm rounded-3xl border border-ink/10 bg-white p-8 text-center shadow-sm">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-hotpink" />
          <h1 className="mt-5 text-xl font-black tracking-tight text-ink">Confirming your payment…</h1>
          <p className="mt-2 text-sm text-ink/60">This only takes a few seconds. Please don't close this page.</p>
        </div>
      </section>
    );
  }

  if (success) {
    return (
      <section className="flex min-h-screen items-center justify-center bg-cream px-6 py-12">
        <div className="w-full max-w-sm rounded-3xl border border-ink/10 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-mint">
            <CheckCircle2 className="h-7 w-7 text-ink" />
          </div>
          <h1 className="mt-5 text-2xl font-black tracking-tight text-ink">{success.free ? "Claimed!" : "Order placed!"}</h1>
          {success.order_number && (
            <p className="mt-2 font-mono text-sm font-bold text-hotpink">{success.order_number}</p>
          )}
          <p className="mt-2 text-sm text-ink/60">
            {success.free
              ? `Thanks${name ? `, ${name.split(" ")[0]}` : ""} — your free download link is on its way to your email.`
              : success.pending
              ? `Thanks${name ? `, ${name.split(" ")[0]}` : ""} — we've let the seller know. Your order will be confirmed once your payment is verified.`
              : success.processing
              ? `Thanks${name ? `, ${name.split(" ")[0]}` : ""} — your payment went through and your order is being confirmed. You'll get an email shortly.`
              : `Thanks${name ? `, ${name.split(" ")[0]}` : ""} — your order has been confirmed.`}
          </p>
          <Link
            to={`/store/${username}`}
            className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-cream hover:bg-ink/85"
          >
            <ArrowLeft size={14} /> Back to store
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-cream px-6 py-12 md:px-14 md:py-16">
      <div className="mx-auto max-w-3xl">
        <Link
          to={`/store/${username}`}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink/60 hover:text-hotpink"
        >
          <ArrowLeft size={14} /> Back to @{username}'s catalog
        </Link>

        <h1 className="mt-4 font-display text-3xl font-black tracking-tight text-ink">Checkout</h1>

        {cart.items.length === 0 ? (
          <div className="mt-10 rounded-3xl border border-dashed border-ink/15 bg-white p-8 text-center">
            <ShoppingBag className="mx-auto h-8 w-8 text-ink/30" />
            <p className="mt-3 text-sm text-ink/50">Your cart is empty.</p>
            <Link
              to={`/store/${username}`}
              className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-cream hover:bg-ink/85"
            >
              Browse products
            </Link>
          </div>
        ) : (
          <form onSubmit={startCheckout} className="mt-6 grid gap-6 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
            <div className="min-w-0 space-y-6">
              <div className="rounded-3xl border border-ink/10 bg-white p-6">
                <h2 className="font-display text-lg font-black tracking-tight">Contact details</h2>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    required
                    className="rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink sm:col-span-2"
                  />
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={cart.hasPhysical ? "Phone number" : "Phone number (optional)"}
                    required={cart.hasPhysical}
                    className="rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                    />
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    type="email"
                    required
                    className="rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                  />
                </div>
              </div>

              {cart.hasPhysical && (
                <div className="rounded-3xl border border-ink/10 bg-white p-6">
                  <h2 className="font-display text-lg font-black tracking-tight">Shipping address</h2>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <input
                      value={line1}
                      onChange={(e) => setLine1(e.target.value)}
                      placeholder="Address line 1"
                      required
                      className="rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink sm:col-span-2"
                    />
                    <input
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="City"
                      required
                      className="rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                    />
                    <input
                      value={addrState}
                      onChange={(e) => setAddrState(e.target.value)}
                      placeholder="State"
                      required
                      className="rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                    />
                    <input
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      placeholder="PIN / ZIP code"
                      required
                      className="rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                    />
                    <input
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      placeholder="Country"
                      required
                      className="rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                    />
                  </div>
                </div>
              )}

              {(store?.plan === "growth" || store?.plan === "pro") && (
              <div className="rounded-3xl border border-ink/10 bg-white p-6">
                <h2 className="font-display text-lg font-black tracking-tight">Discount code</h2>
                <div className="mt-4 flex items-center gap-2">
                  <Tag size={15} className="flex-none text-ink/40" />
                  <input
                    value={discountCode}
                    onChange={(e) => {
                      setDiscountCode(e.target.value.toUpperCase());
                      setDiscountStatus("idle");
                      setDiscountError("");
                    }}
                    placeholder="Optional code"
                    className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-hotpink"
                  />
                  {discountStatus === "applied" ? (
                    <button
                      type="button"
                      onClick={clearDiscount}
                      className="flex-none rounded-2xl border border-ink/15 px-4 py-2.5 text-sm font-semibold text-ink/60 hover:bg-ink/5"
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={applyDiscountCode}
                      disabled={!discountCode.trim() || discountStatus === "checking"}
                      className="flex-none rounded-2xl bg-ink px-4 py-2.5 text-sm font-semibold text-cream hover:bg-ink/85 disabled:opacity-40"
                    >
                      {discountStatus === "checking" ? <Loader2 size={15} className="animate-spin" /> : "Apply"}
                    </button>
                  )}
                </div>
                {discountStatus === "applied" && preview && (
                  <p className="mt-2 text-xs font-semibold text-emerald-600">
                    Code applied — you save {formatMoney(preview.discount_amount, preview.currency)}.
                  </p>
                )}
                {discountStatus === "error" && (
                  <p className="mt-2 text-xs font-semibold text-hotpink">{discountError}</p>
                )}
              </div>
              )}

              {errors.length > 0 && (
                <div className="rounded-2xl border border-hotpink/30 bg-hotpink/5 p-4 text-sm text-hotpink">
                  <ul className="list-disc pl-4 space-y-1">
                    {errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="h-fit rounded-3xl border border-ink/10 bg-white p-6">
              <h2 className="font-display text-lg font-black tracking-tight">Order summary</h2>
              <div className="mt-4 space-y-3">
                {cart.items.map((line) => (
                  <div key={`${line.catalog_item_id}-${line.size || ""}`} className="flex items-start justify-between gap-2 text-sm">
                    <span className="min-w-0 break-words text-ink/70">
                      {line.name}
                      {line.size ? ` · ${line.size}` : ""}{line.is_digital ? "" : ` × ${line.quantity}`}
                    </span>
                    <span className="flex-none font-semibold text-ink">{formatMoneyOrFree(line.price * line.quantity, currency)}</span>
                  </div>
                ))}
              </div>

              {/* Full breakdown is always shown once loaded -- shipping and
                  total are never a surprise at payment time, discount code
                  or not. */}
              {preview ? (
                <div className="mt-4 space-y-1.5 border-t border-ink/10 pt-4 text-sm">
                  <div className="flex items-center justify-between text-ink/70">
                    <span>Subtotal</span>
                    <span>{formatMoneyOrFree(preview.subtotal, preview.currency)}</span>
                  </div>
                  {preview.discount_amount > 0 && (
                    <div className="flex items-center justify-between font-semibold text-emerald-600">
                      <span>Discount{appliedCode ? ` (${appliedCode})` : ""}</span>
                      <span>-{formatMoney(preview.discount_amount, preview.currency)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-ink/70">
                    <span>Shipping</span>
                    <span>{preview.shipping_fee_charged > 0 ? formatMoney(preview.shipping_fee_charged, preview.currency) : "FREE"}</span>
                  </div>
                  <div className="flex items-center justify-between pt-1.5 text-base font-black text-ink">
                    <span>Total</span>
                    <span>{formatMoneyOrFree(preview.total_amount, preview.currency)}</span>
                  </div>
                </div>
              ) : previewError ? (
                <div className="mt-4 border-t border-ink/10 pt-4 text-sm">
                  <p className="text-hotpink">Couldn't calculate shipping & total.</p>
                  <button
                    type="button"
                    onClick={() => fetchPreview(appliedCode)}
                    className="mt-1.5 text-xs font-semibold text-hotpink underline decoration-dotted"
                  >
                    Try again
                  </button>
                </div>
              ) : (
                <div className="mt-4 flex items-center gap-2 border-t border-ink/10 pt-4 text-sm text-ink/50">
                  {previewLoading && <Loader2 size={13} className="animate-spin" />}
                  Calculating shipping & total…
                </div>
              )}

              <button
                type="submit"
                disabled={placing || storeStatus !== "ok" || !preview}
                className="mt-5 flex w-full items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-5 py-3 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50"
              >
                {placing ? <Loader2 size={15} className="animate-spin" /> : null}
                {placing
                  ? "Processing…"
                  : preview
                  ? Number(preview.total_amount) === 0
                    ? "Claim for free"
                    : `Review & Pay ${formatMoney(preview.total_amount, preview.currency)}`
                  : "Pay now"}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Confirm-before-you-pay step: the order already exists (with a real
          order_number) by the time this shows. Razorpay only opens once the
          customer taps "Pay" here. */}
      {confirmOpen && checkoutData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-4">
          <div className="w-full max-w-sm rounded-3xl border border-ink/10 bg-white p-6 shadow-xl">
            <h2 className="font-display text-xl font-black tracking-tight text-ink">Confirm your order</h2>
            {checkoutData.order_number && (
              <p className="mt-1 font-mono text-sm font-bold text-hotpink">{checkoutData.order_number}</p>
            )}

            <div className="mt-4 space-y-1.5 rounded-2xl bg-ink/[0.03] p-4 text-sm">
              <div className="flex items-center justify-between text-ink/70">
                <span>Subtotal</span>
                <span>{formatMoney(checkoutData.subtotal, checkoutData.currency)}</span>
              </div>
              {checkoutData.discount_amount > 0 && (
                <div className="flex items-center justify-between font-semibold text-emerald-600">
                  <span>Discount</span>
                  <span>-{formatMoney(checkoutData.discount_amount, checkoutData.currency)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-ink/70">
                <span>Shipping</span>
                <span>
                  {checkoutData.shipping_fee_charged > 0
                    ? formatMoney(checkoutData.shipping_fee_charged, checkoutData.currency)
                    : "FREE"}
                </span>
              </div>
              <div className="flex items-center justify-between border-t border-ink/10 pt-1.5 text-base font-black text-ink">
                <span>Total</span>
                <span>{formatMoney(checkoutData.total_amount, checkoutData.currency)}</span>
              </div>
            </div>

            {["upi", "qr", "bank"].includes(checkoutData.payment_method) ? (
              <>
                <div className="mt-4 space-y-2.5 rounded-2xl border border-dashed border-hotpink/30 bg-hotpink/5 p-4 text-sm">
                  {checkoutData.payment_method === "upi" && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-ink/50">Pay via UPI</p>
                      <p className="mt-1 font-mono text-sm font-bold text-ink">
                        {checkoutData.payment_instructions?.upi_id || "UPI ID not set"}
                      </p>
                    </div>
                  )}
                  {checkoutData.payment_method === "qr" && checkoutData.payment_instructions?.qr_code_image_url && (
                    <div className="text-center">
                      <p className="text-xs font-bold uppercase tracking-wide text-ink/50">Scan to pay</p>
                      <img
                        src={checkoutData.payment_instructions.qr_code_image_url}
                        alt="Payment QR code"
                        className="mx-auto mt-2 h-40 w-40 rounded-xl border border-ink/10 object-contain"
                      />
                    </div>
                  )}
                  {checkoutData.payment_method === "bank" && (
                    <div className="space-y-1">
                      <p className="text-xs font-bold uppercase tracking-wide text-ink/50">Bank transfer</p>
                      <p className="text-ink/80">Account name: <span className="font-semibold text-ink">{checkoutData.payment_instructions?.bank_account_name || "—"}</span></p>
                      <p className="text-ink/80">Account number: <span className="font-mono font-semibold text-ink">{checkoutData.payment_instructions?.bank_account_number || "—"}</span></p>
                      <p className="text-ink/80">IFSC: <span className="font-mono font-semibold text-ink">{checkoutData.payment_instructions?.bank_ifsc || "—"}</span></p>
                      <p className="text-ink/80">Bank: <span className="font-semibold text-ink">{checkoutData.payment_instructions?.bank_name || "—"}</span></p>
                      {checkoutData.payment_instructions?.bank_instructions && (
                        <p className="mt-1 text-xs text-ink/60">{checkoutData.payment_instructions.bank_instructions}</p>
                      )}
                    </div>
                  )}
                  <p className="pt-1 text-xs text-ink/60">
                    Pay the total above, attach a screenshot of the payment, then tap "I've paid" — the seller will verify and confirm your order.
                  </p>
                </div>

                <div className="mt-3">
                  <label
                    htmlFor="payment-screenshot-input"
                    className="flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed border-ink/20 bg-ink/[0.02] px-4 py-3 text-xs font-semibold text-ink/60 hover:bg-ink/5"
                  >
                    {screenshotPreview ? (
                      <img src={screenshotPreview} alt="Payment screenshot preview" className="h-14 w-14 rounded-lg object-cover" />
                    ) : (
                      <span>Tap to attach payment screenshot</span>
                    )}
                  </label>
                  <input
                    id="payment-screenshot-input"
                    type="file"
                    accept="image/*"
                    onChange={handleScreenshotChange}
                    className="hidden"
                  />
                  {screenshotError && (
                    <p className="mt-1.5 text-xs font-medium text-red-500">{screenshotError}</p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={markAsPaid}
                  disabled={markingPaid}
                  className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-5 py-3 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50"
                >
                  {markingPaid ? <Loader2 size={15} className="animate-spin" /> : null}
                  {markingPaid ? "Confirming…" : "I've paid"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmOpen(false);
                    setScreenshotFile(null);
                    setScreenshotPreview(null);
                    setScreenshotError("");
                  }}
                  disabled={markingPaid}
                  className="mt-2 w-full rounded-full px-5 py-2 text-center text-xs font-semibold text-ink/50 hover:text-ink disabled:opacity-40"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={proceedToPayment}
                  disabled={placing}
                  className="mt-5 flex w-full items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-5 py-3 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50"
                >
                  {placing ? <Loader2 size={15} className="animate-spin" /> : null}
                  {placing ? "Opening payment…" : `Pay ${formatMoney(checkoutData.total_amount, checkoutData.currency)} now`}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmOpen(false)}
                  disabled={placing}
                  className="mt-2 w-full rounded-full px-5 py-2 text-center text-xs font-semibold text-ink/50 hover:text-ink disabled:opacity-40"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
